import {
  Box,
  Button,
  Grid,
  TextField,
  Paper,
  MenuItem,
  Divider,
  alpha,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { MdPeople } from "react-icons/md";
import { GifBox, Search } from "@mui/icons-material";
import WSTextField from '../../Components/TextBox/WSTextBox';
import { useAuth } from "../../StateManagement/authContext";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { debounce } from "lodash";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import SectionHeader from "../components/header";
import { WSIcons } from "../../commonFunctions/iIcons";
import DataTable, { Column } from "../components/dataTable";
import ConfirmationPopup from "../../Components/Confirmation/Confirmation";

export type CDS = {
  _id?: string;
  name: string;
  code: string;
  blockId: string;
};

const CDSManager = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cdsList, setCdsList] = useState<CDS[]>([]);
  const [editId, setEditId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5)
  const [isPopupOpen, setPopupOpen] = useState(false); //for delete;
  const [selectedIdForDlt, setSelectedIdForDlt] =
    useState<any>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOption, setSortOption] = useState("created_desc");

  const { handleSubmit, reset, setValue, control } = useForm<CDS>();
  const { showMessage } = useSnackbar();
  const { user } = useAuth();


  const handleEdit = (cds: CDS) => {
    setValue("name", cds.name);
    setValue("code", cds.code);
    setEditId(cds._id || null)
  };


  const fetchData = useCallback(
    debounce(async (pageVal: number, searchVal: string, limitVal: number, statusVal: string, sortVal: string) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          page: String(pageVal),
          search: searchVal,
          limit: String(limitVal),
          ...(statusVal && { status: statusVal }),
          ...(sortVal && { sort: sortVal }),
        });

        const res = await callAPI(
          `cds/getPaginated?${query.toString()}`,
          Method.GET
        );

        if (res.success) {
          setCdsList(res.cds || []);
          setTotalPages(res.totalPages || 0);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    }, 400),
    [setLoading, setCdsList, setTotalPages]
  );

  const onSubmit = async (data: CDS) => {
    try {
      setSubmitting(true);

      let request = {
        name: data.name,
        code: data.code,
        block: user?.block,
      }

      if (editId) {
        await callAPI(`cds/update/${editId}`, Method.PUT, request);
        showMessage("CDS Updated Successfully", "success");
      } else {
        await callAPI("cds/create", Method.POST, request);
        showMessage("New CDS Created Successfully", "success");
      }

      reset();
      setEditId(null);
      fetchData(page, search, rowsPerPage, statusFilter, sortOption);
    } catch (error: any) {
      if (error.message === 'GlobalHandledError') return;
      console.error("Error while submitting cds:", error);
      showMessage(error?.response?.data?.message || "An unexpected error occurred", "error");
    } finally {
      setSubmitting(false);
    }
  };


  const handleDelete = () => {
    showMessage("District Deleted Successfully", "success");
    fetchData(page, search, rowsPerPage, statusFilter, sortOption);
  };

  const handleSortChange = (value: string) => {
    setSortOption(value);
    setPage(1);
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  useEffect(() => {
    fetchData(page, search, rowsPerPage, statusFilter, sortOption);
  }, [page, search, rowsPerPage, statusFilter, sortOption]);


  const confirmDelete = (data: any) => {
    setSelectedIdForDlt(data._id);
    setPopupOpen(true)
  };

  const handleCancel = () => {
    setEditId(null);
    reset();
  }

  const columns: Column[] = [
    { field: "code", label: "Code" },
    { field: "name", label: "Name" }
    // {
    //   field: "createdAt",
    //   label: "Created",
    //   sortable: true,
    //   type: "date",
    // },
  ];


  const actions = [
    { label: "Edit", handler: handleEdit },
    { label: "Delete", handler: confirmDelete },
  ]


  const getButtonText = () => {
    if (submitting && editId) return "Updating...";
    if (submitting) return "Creating...";
    if (editId) return "Update CDS";
    return "Create New CDS";
  };

  return (
    <Box p={3} mb={6}>
      <SectionHeader
        title={"CDS Management"} icon={<MdPeople fontSize={"large"} />} subtitle={editId ? "✏️ Update CDS" : "➕ Add New CDS"}
        count={cdsList.length}
        gradient={{ from: '#4AD16E', to: '#08B1B6' }}
        borderGradient={{ from: '#08B1B6', to: '#4AD16E' }}
        textColor="#fff"
      />
      {/* Form */}
      <Paper elevation={0} sx={{ p: 2, mb: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <WSTextField
                id="cds"
                label="CDS Name *"
                name="name"
                control={control}
                rules={{ required: "CDS Name is required" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <WSTextField
                id="code"
                label="CDS Code *"
                name="code"
                control={control}
                rules={{ required: "CDS code is required" }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="medium"
                sx={{
                  textTransform: "none",
                  color: "white",
                  borderImage: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
                  borderImageSlice: 1,
                  backgroundImage: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
                  "&:hover": {
                    backgroundImage: "linear-gradient(95.53deg, #4DD26B 0%, #08B1B6 99.12%)",
                    opacity: 0.9,
                  },
                }}
                disabled={submitting}
              >
                {getButtonText()}
              </Button>
              {editId && (
                <Button size="medium" onClick={handleCancel} sx={{ ml: 2 }} color="secondary">
                  Cancel
                </Button>
              )}
            </Grid>
          </Grid>

        </form>
      </Paper>

      <DataTable
        columns={columns}
        data={cdsList}
        loading={loading}
        onPageChange={setPage}
        sort={{
          options: [
            { value: 'name_asc', label: 'Name (A → Z)' },
            { value: 'name_desc', label: 'Name (Z → A)' },
            { value: 'created_desc', label: 'Newest' },
            { value: 'created_asc', label: 'Oldest' }
          ],
          defaultValue: 'created_desc',
          value: sortOption,
          onChange: handleSortChange
        }}
        search={{
          fieldName: 'searchQuery',
          onChange: handleSearchChange,
          placeholder: 'Search receipt...'
        }}
        page={page}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalPages={totalPages}
        actions={actions}
        exportToExcel
      />


      {
        isPopupOpen && (
          <ConfirmationPopup
            apiUrl={"cds/delete"}
            id={selectedIdForDlt}
            confirmMessage="Are you sure you want to delete this cds?"
            open={isPopupOpen}
            onClose={() => setPopupOpen(false)}
            onConfirm={handleDelete}
            permanent
          />
        )
      }
    </Box >
  );
};

export default CDSManager;
